import { useExamResultStore } from '../exams/store';
import { findGeneratedDocument, useDynamicDocumentStore } from '../filmstrip/documents';
import { periodKey, useOpenedDocumentStore } from '../filmstrip/opened';
import { taskCoverDocument, taskHasUnit, type SubmissionTask } from '../submissions/submissions';
import type { BoardContextValue } from '../useBoardContext';
import { useSelectUnit } from '../useSelectUnit';
import { sessionTarget } from './sessions';
import { useResultSessionStore } from './store';

// Opening a bài tập or bài kiểm tra puts its đề on the board as a tab, its results beside it
export function useResultActions(context: Pick<BoardContextValue, 'unitId' | 'date' | 'index' | 'classId'>) {
  const selectUnit = useSelectUnit();

  /** Shows the tab: the student it left open, else its đề. */
  function show(tabId: string, title: string, courseId?: string) {
    const target = sessionTarget(useResultSessionStore.getState().sessions, tabId);
    const document = target === tabId ? null : findGeneratedDocument(target);
    selectUnit({ unitId: target, unitName: document ? document.title : title, courseId });
  }

  /** False when the exam's test unit is unknown, so there is no đề to open. */
  function openExam(examId: string): boolean {
    const state = useExamResultStore.getState();
    const unit = state.examUnits[examId];
    if (!unit) return false;
    const title = unit.title || state.examTitles[examId] || 'Bài kiểm tra';
    useResultSessionStore.getState().open({ tabId: unit.unitId, kind: 'exam', refId: examId, title });
    show(unit.unitId, title, unit.courseId);
    return true;
  }

  function openHomework(task: SubmissionTask, courseTitle: string) {
    let tabId = task.id;
    // no IZ unit means no đề: a painted cover stands in as the tab, on this period's strip
    if (!taskHasUnit(task)) {
      const cover = useDynamicDocumentStore.getState().openTransient(taskCoverDocument(task, courseTitle));
      useOpenedDocumentStore.getState().record({ period: periodKey(context.date, context.index, context.classId), id: cover.id, title: cover.title, kind: 'slide' });
      tabId = cover.id;
    }
    useResultSessionStore.getState().open({ tabId, kind: 'homework', refId: task.id, title: task.title });
    show(tabId, task.title, task.courseId);
  }

  /** Back to the đề in the same tab. */
  function backToDe(tabId: string) {
    const session = useResultSessionStore.getState().sessions[tabId];
    if (!session) return;
    if (session.view && session.view === context.unitId) selectUnit({ unitId: tabId, unitName: session.title });
    useResultSessionStore.getState().closeStudent(tabId);
  }

  return { openExam, openHomework, backToDe };
}
